import { useCallback, useEffect, useRef, useState } from "react";
import { rpc } from "../lib/supabase.js";
import {
  emptyState,
  loadAcademy,
  loadStudent,
  persistChanges,
  registerStudent as saveRegistration,
  confirmPendingEnrollment as confirmPending,
  loadExamFolders, saveExamSchedule, getExamSaveResult, removeExamPeriod, removeExamFolder,
} from "../lib/academyRepository.js";

export function useAcademyStore(context) {
  const [state, setState] = useState(emptyState);
  const [selectedStudentId, selectStudent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const snapshot = useRef(state);
  const selected = useRef("");
  const busy = useRef(false);
  const revision = useRef(0);
  const examEditorGuard = useRef(false);
  const setExamEditorGuard = useCallback((active) => { examEditorGuard.current = active; }, []);
  const isExamEditing = useCallback(() => examEditorGuard.current, []);
  const publish = useCallback((next) => {
    snapshot.current = next;
    setState(next);
  }, []);
  const refresh = useCallback(async () => {
    if (examEditorGuard.current) {
      setError("시험 일정 입력 내용이 남아 있습니다. 저장 결과를 확인하거나 입력 화면을 닫은 뒤 다시 조회해 주세요.");
      return;
    }
    const request = ++revision.current;
    setLoading(true);
    setError("");
    try {
      let next = await loadAcademy(context);
      if (
        selected.current &&
        next.students.some((student) => student.id === selected.current)
      )
        next = await loadStudent(selected.current, next);
      if (request === revision.current) publish(next);
    } catch (failure) {
      if (request === revision.current) {
        publish(emptyState());
        setError(failure.message);
      }
    } finally {
      if (request === revision.current) setLoading(false);
    }
  }, [context, publish]);
  const refreshSchools = useCallback(async () => {
    const request = revision.current;
    const schools = await rpc("list_academy_schools");
    if (request !== revision.current) throw new Error("학교 목록을 다시 확인해 주세요.");
    // No global loading/unmount: preserve the student's unsaved form.
    publish({ ...snapshot.current, academyInfo: { ...snapshot.current.academyInfo, schools } });
    return schools;
  }, [context, publish]);
  useEffect(() => {
    refresh();
    return () => {
      revision.current++;
    };
  }, [refresh]);
  const setSelectedStudentId = useCallback(
    async (id) => {
      selected.current = id;
      selectStudent(id);
      if (!id) return;
      const request = ++revision.current;
      setLoading(true);
      setError("");
      try {
        const next = await loadStudent(id, snapshot.current);
        if (request === revision.current) publish(next);
      } catch (failure) {
        if (request === revision.current) {
          setError(failure.message);
          selected.current = "";
          selectStudent("");
        }
      } finally {
        if (request === revision.current) setLoading(false);
      }
    },
    [publish],
  );
  const update = useCallback(
    async (recipe, feedback = {}) => {
      if (busy.current) return false;
      busy.current = true;
      setSaving(true);
      setError("");
      const before = snapshot.current;
      const after = structuredClone(before);
      try {
        recipe(after);
        await persistChanges(before, after, context);
        try {
          let next = await loadAcademy(context);
          if (
            selected.current &&
            next.students.some((student) => student.id === selected.current)
          )
            next = await loadStudent(selected.current, next);
          publish(next);
        } catch (failure) {
          if (!feedback.onSavedWarning) throw failure;
          feedback.onSavedWarning("수업 저장은 완료됐지만 목록을 불러오지 못했습니다. 팝업을 닫고 다시 조회해 주세요.");
        }
        return true;
      } catch (failure) {
        if (feedback.onError) feedback.onError(failure.message);
        else setError(failure.message);
        try {
          let next = await loadAcademy(context);
          if (
            selected.current &&
            next.students.some((student) => student.id === selected.current)
          )
            next = await loadStudent(selected.current, next);
          publish(next);
        } catch {
          /* Keep last confirmed server snapshot. */
        }
        return false;
      } finally {
        busy.current = false;
        setSaving(false);
      }
    },
    [context, publish],
  );
  const registerStudent = useCallback(async (id, form) => {
    if (busy.current) return null;
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      const result = await saveRegistration(id, form, context);
      // A committed registration remains successful even if the following read fails.
      try { publish(await loadAcademy(context)); }
      catch { setError("학생 등록은 완료됐지만 목록을 불러오지 못했습니다. 새로고침해 주세요."); }
      return result.studentId;
    } catch (failure) {
      setError(failure.message);
      return null;
    } finally { busy.current = false; setSaving(false); }
  }, [context, publish]);
  const confirmPendingEnrollment = useCallback(async (id, setup) => {
    if (busy.current) return false;
    busy.current = true; setSaving(true); setError("");
    try {
      await confirmPending(id, setup);
      try { publish(await loadStudent(selected.current, snapshot.current)); }
      catch { setError("수업 저장은 완료됐지만 이력을 불러오지 못했습니다. 다시 조회해 주세요."); }
      return true;
    } catch (failure) { setError(failure.message); return false; }
    finally { busy.current = false; setSaving(false); }
  }, [publish]);
  const saveExamPeriod = useCallback(async (request) => {
    if (busy.current) return null;
    busy.current = true; setSaving(true); setError("");
    try {
      const result = await saveExamSchedule(request);
      try { publish({ ...snapshot.current, examFolders: await loadExamFolders() }); }
      catch { setError("저장은 완료됐지만 목록을 불러오지 못했습니다. 다시 조회해 주세요."); }
      return result;
    } catch (failure) {
      setError(failure.message);
      return failure.code === "EXAM_SAVE_UNCONFIRMED" ? {status:"unknown",requestId:request.requestId} : null;
    } finally { busy.current=false; setSaving(false); }
  }, [context,publish]);
  const deleteExam = useCallback(async (id, folder=false) => {
    if (busy.current) return false;
    busy.current=true; setSaving(true); setError("");
    try {
      await (folder ? removeExamFolder(id) : removeExamPeriod(id));
      try { publish({...snapshot.current,examFolders:await loadExamFolders()}); }
      catch { setError("삭제는 완료됐지만 목록을 불러오지 못했습니다. 다시 조회해 주세요."); }
      return true;
    } catch (failure) { setError(failure.message); return false; }
    finally { busy.current=false; setSaving(false); }
  }, [context,publish]);
  return {
    state,
    context,
    loading,
    saving,
    error,
    refresh,
    refreshSchools,
    update,
    registerStudent,
    confirmPendingEnrollment,
    saveExamPeriod,
    setExamEditorGuard,
    isExamEditing,
    getExamSaveResult,
    deleteExamPeriod:(id)=>deleteExam(id),
    deleteExamFolder:(id)=>deleteExam(id,true),
    selectedStudentId,
    selectedStudent:
      state.students.find((student) => student.id === selectedStudentId) ||
      null,
    setSelectedStudentId,
    createId: () => crypto.randomUUID(),
  };
}
